import { customsTaxService } from '@services/requests/customsTaxService';
import { CountriesResponse } from '@services/requests/customsTaxService/interface';
import React, { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { ServiceProps } from '../types';
import { CustomsPaymentsDetailingMain } from './CustomsPaymentsDetailing/CustomsPaymentsDetailingMain';
import { CustomsPaymentsDetailingOverall } from './CustomsPaymentsDetailing/CustomsPaymentsDetailingOverall';
import { CustomsPaymentsDetailingProductHelp } from './CustomsPaymentsDetailing/CustomsPaymentsDetailingProductHelp';
import { CustomsPaymentsDetailingResult } from './CustomsPaymentsDetailing/CustomsPaymentsDetailingResult';
import { CustomsPaymentsDetailingStyledContainer } from './CustomsPaymentsDetailing/StyledComponents';
import { CustomsPaymentsProductList } from './CustomsPaymentsProductList';
import { CustomsPaymentsTemplateStore } from './types';

const CustomsPaymentsTemplate: FC<ServiceProps> = (props) => {
  const {
    service,
    serviceTemplateStore,
    isOrderDisplay,
    servicesFieldValues,
    servicesFields,
    globalFieldValues,
    globalFields,
    options,
  } = props;

  const [customsNotesContainer, setCustomsNotesContainer] = useState<HTMLDivElement>(null);
  const [countries, setCountries] = useState<CountriesResponse>([]);

  useEffect(() => {
    customsTaxService().LoadCountries().then(setCountries);
  }, []);

  useEffect(() => {
    const containerNode = document.querySelector(
      '#customs-payments-notes-container'
    ) as HTMLDivElement;

    if (containerNode) {
      setCustomsNotesContainer(containerNode);
    }
  }, []);

  const store: CustomsPaymentsTemplateStore = serviceTemplateStore[service.id];
  const isLastProduct = store.products.length === 1;

  const fields = [...servicesFields, ...globalFields];
  const fieldValues = { ...globalFieldValues, ...servicesFieldValues };
  const settings = service.settings.TemplateSettings.CustomsPayments;

  const contractCurrencyField = fields.find((f) => f.code === settings.ContractCurrencyField);
  const borderDeliveryCurrencyField = fields.find(
    (f) => f.code === settings.BorderDeliveryCurrencyField
  );
  const otherExpensesCurrencyField = fields.find(
    (f) => f.code === settings.OtherExpensesCurrencyField
  );

  const courseFields = fields.filter(
    (f) => f.settings.DefaultSettings.NumberFieldSettings.IsCourseRequired
  );

  const allCurrencies = [
    ...options[contractCurrencyField.code],
    ...options[borderDeliveryCurrencyField.code],
    ...options[otherExpensesCurrencyField.code],
    ...courseFields
      .map((f) => f.settings.DefaultSettings.NumberFieldSettings.CurrencyField)
      .map((fieldCode) => options[fieldCode])
      .flat(),
  ].map((option) => option.origin);

  fields
    .filter((f) => f.settings.DefaultSettings.NumberFieldSettings.IsCurrencyCourseField)
    .forEach((f) => {
      const currencies = courseFields.map(
        (c) => fieldValues[c.settings.DefaultSettings.NumberFieldSettings.CurrencyField]
      );
      const fieldCurrency =
        fieldValues[f.settings.DefaultSettings.NumberFieldSettings.CurrencyField];

      if (!currencies.includes(fieldCurrency)) {
        courseFields.push(f);
      }
    });

  const currencies = courseFields
    .map((f) => [
      fieldValues[f.settings.DefaultSettings.NumberFieldSettings.CurrencyField],
      fieldValues[f.code],
    ])
    .map(([currencyId, course]) => {
      const foundCurrency = allCurrencies.find((currency) => currency.id === currencyId.toString());

      if (!foundCurrency) {
        return null;
      }

      return { ...foundCurrency, course };
    })
    .filter(Boolean);

  if (isOrderDisplay) {
    return (
      <CustomsPaymentsDetailingStyledContainer>
        <div className="customs-payments-wrap">
          <CustomsPaymentsDetailingOverall {...props} />
          {store.products.map((product, index) => (
            <div key={'product-detailing-' + index}>
              <CustomsPaymentsDetailingMain
                product={product}
                index={index}
                countries={countries}
                isLastProduct={isLastProduct}
                {...props}
              />
              <CustomsPaymentsDetailingResult
                currencies={currencies}
                product={product}
                productIndex={index}
                {...props}
              />
            </div>
          ))}
        </div>
        {customsNotesContainer &&
          createPortal(
            <CustomsPaymentsDetailingProductHelp products={store.products} />,
            customsNotesContainer
          )}
      </CustomsPaymentsDetailingStyledContainer>
    );
  }

  return (
    <CustomsPaymentsProductList
      isLastProduct={isLastProduct}
      products={store.products}
      countries={countries}
      currencies={currencies}
      {...props}
    />
  );
};

export default CustomsPaymentsTemplate;
